import Link from "next/link";
import { usePopularGames } from "@/api/game-api";
import type { PopularGamesPlatform } from "@/pages/api/games/popular";
import { useTrans } from "@/utils/useTrans";
import GameCard from "@/v2-components/pages/games/GameCard";
import { SUPPORTED_GAMES_HREF } from "@/v2-components/pages/games/GamesSubNav";
import { Skeleton } from "@/v2-components/ui/skeleton";
import { cn } from "@/libs/utils";

/**
 * GNB 2deps 「지금 인기 PC/VR 타이틀」 레일.
 *
 * ## 왜 기존 컴포넌트를 재사용하지 않나
 *
 * 후보였던 `DisplayGameCardsGrid`(`grid-cols-3` 하드코딩)와 `DisplayWeeklyGames`(h2 ·
 * autoplay 캐러셀 · 순위 오버레이 · `SelectGameKeyDialog` 까지 붙은 **페이지 섹션**)가
 * 둘 다 GNB 패널엔 과했다. 여기서 필요한 건 **가로 레일 하나**뿐이다.
 *
 * ⚠️ 그 둘은 **지금 없다** — #5689 가 레거시 게임 홈과 함께 지웠다(2026-09-16). 이 문단은
 * 「재사용 대신 새로 만든 이유」의 기록이지 현재 코드에 대한 주장이 아니다. 즉 이 판단을
 * 되돌릴 때 **재사용할 대상이 애초에 없다.**
 *
 * 카드 자체(`GameCard`)와 스켈레톤 패턴은 그대로 재사용한다.
 *
 * ## 게임 키 배너를 붙이지 않는다
 *
 * `GameCard.ResellBanner` 는 클릭 시 `SelectGameKeyDialog` 를 여는데, 그러면 **시트 위에
 * 모달이 겹친다.** 시안에도 배너가 없다(#5349 §9 #2 — 미노출로 확정).
 *
 * ## 썸네일
 *
 * `direction="vertical"` 이면 `GameCard.Image` 가 **`game.thumbnailLink`** 를 쓴다.
 * 그게 실제로 세로 이미지다(Steam `library_600x900_2x.jpg`).
 * ⚠️ 이슈 초안의 `thumbnailPortraitImageLink` 는 **`GameResell` 소유 필드**이고
 * `BhapticsContent` 응답엔 키 자체가 없다 — 쓰면 안 된다.
 *
 * `GameCard.Image` 의 기본 비율은 `aspect-game-card-vertical`(**96/148**)이라 시안 값과
 * 다르다. 호출처에서 덮는다 — `cn` 이 호출처 우선이라 같은 브레이크포인트에서 해소된다.
 */
export function PopularTitlesRail({
  platform,
  onClick,
  count,
  variant = "desktop",
}: {
  platform: PopularGamesPlatform;
  /**
   * 이동 시 패널을 닫는다. 같은 패널의 다른 이동 표현(`LinkCard`·링크 그룹)이 전부
   * 이걸 받으므로 여기만 빠지면 **한 패널 안에서 동작이 갈린다** — 인기 타이틀만
   * 눌러도 패널이 열린 채 남는다(#5386 이 모바일에서 고친 그 증상).
   */
  onClick: (e: any) => void;
  /** 시안(디자인 파일) 기준 데스크톱 4장 · 모바일 3장. 넘기면 그 값이 이긴다. */
  count?: number;
  /**
   * ★**데스크톱과 모바일이 카드 크기부터 여백까지 전부 다르다** — 값을 하나씩
   * 프롭으로 열면 호출처 둘이 서로 다른 조합을 만들 수 있어, **묶어서 하나**로 받는다.
   * 모바일 시안은 `29304:21613`(PC) · `29304:241268`(VR).
   */
  variant?: RailVariant;
}) {
  const { t } = useTrans(["gnb"]);
  const v = VARIANTS[variant];
  const n = count ?? v.count;
  const { games, isLoading } = usePopularGames(platform, n);

  const label =
    platform === "pc" ? "지금 인기 PC 타이틀" : "지금 인기 VR 타이틀";

  return (
    <div className={v.root}>
      <div className={v.labelRow}>
        <h4 className={v.label}>{t(label)}</h4>
        {v.seeAll && (
          <Link
            href={SUPPORTED_GAMES_HREF[platform]}
            onClick={onClick}
            className="shrink-0 text-xs font-500 leading-4 tracking-[0.5px] text-blue-60 transition-colors hover:text-blue-hover-70"
          >
            {t(SEE_ALL_LABEL[platform])}
          </Link>
        )}
      </div>

      <div className={v.rail}>
        {isLoading &&
          Array.from({ length: n }).map((_, i) => (
            <Skeleton
              key={i}
              // 스켈레톤도 카드와 **같은 비율**이어야 로딩 → 로딩완료에서 시트 높이가
              // 튀지 않는다. 시트는 패널 높이를 px 로 재서 트랜지션한다.
              className={cn("shrink-0 rounded-[8px]", v.card)}
            />
          ))}

        {!isLoading &&
          games.map((game) => (
            <GameCard key={game.id} game={game}>
              <Link
                href={`/games/experiences/${game.slug}`}
                onClick={onClick}
                className="shrink-0"
              >
                <GameCard.Image
                  direction="vertical"
                  className={v.card}
                  // 게임 키 배너를 안 붙이므로 상태 배지(New/Upcoming)만 남긴다.
                />
              </Link>
            </GameCard>
          ))}
      </div>
    </div>
  );
}

type RailVariant = "desktop" | "mobile";

/**
 * 시안이 `label-row`(데스크탑 `29464:21689` PC · `29464:21692` VR) 오른쪽 끝에 새로 넣은
 * 링크. 노드 id 가 `30363:*` 라 라벨(`29230:*`)보다 나중에 얹힌 개정이다.
 *
 * 문구는 시안이 두 플랫폼 다 `See All Supported Games` 인데 **`PC`/`VR` 을 넣는다**
 * (2026-09-07 요청). 서브내비·GNB 3항목이 이미 `Supported PC Games` /
 * `Supported VR Games` 로 갈라 쓰고 있어(#5582) 그쪽과 한 벌이 된다.
 *
 * ⚠️ **모바일엔 넣지 않는다**(요청). 그래서 `VARIANTS.mobile.seeAll = false` 다 —
 * 프롭이 아니라 variant 필드인 이유는 이 컴포넌트가 화면별 차이를 **묶어서** 받기
 * 때문이다(위 `variant` 프롭 주석).
 */
const SEE_ALL_LABEL = {
  pc: "See All Supported PC Games",
  vr: "See All Supported VR Games",
} as const;

/**
 * 시안 실측 — 데스크탑 `159×238`(`29230:21392`) · 모바일 `96×144`(`29304:21613`).
 * 둘 다 `GameCard.Image` 기본값(`aspect-game-card-vertical` = 96/148)과 다르므로 덮는다.
 *
 * 모바일은 패널 안쪽이라 좌우 여백을 스스로 갖는다 — 링크 행과 같은 `pl-9 pr-4`.
 */
const VARIANTS: Record<
  RailVariant,
  {
    root: string;
    /** 라벨과 (있으면) `See All` 을 담는 행. 시안 `label-row`. */
    labelRow: string;
    label: string;
    rail: string;
    card: string;
    count: number;
    /** 시안이 데스크탑에만 넣었고, 요청도 데스크탑만이다. */
    seeAll: boolean;
  }
> = {
  desktop: {
    root: "flex flex-col gap-y-3",
    // 시안 `29464:21689` — `flex items-center justify-between`. 레일 전폭(708)에서
    // 라벨이 좌, 링크가 우로 끝에 붙는다(x=564 + w=144 = 708).
    labelRow: "flex items-center justify-between",
    // ★종전 `text-sm`(12/16 · 400 · 자간 없음)에서 시안 값으로 맞췄다 —
    // `label-row` 는 좌우 **둘 다** 11/16 · 500 · `+0.5` 다. 모바일은 이미 그 값이라
    // 데스크탑만 어긋나 있었다.
    label: "text-xs font-500 leading-4 tracking-[0.5px] text-coolgray-50",
    rail: "flex gap-x-6",
    card: "w-[159px] aspect-[159/238]",
    count: 4,
    seeAll: true,
  },
  mobile: {
    root: "flex flex-col",
    // 모바일은 `See All` 이 없어 행에 자식이 하나다 — 여백은 `label` 이 갖는다.
    labelRow: "",
    label:
      "pb-2 pl-9 pr-4 pt-3 text-xs font-500 leading-4 tracking-[0.5px] text-coolgray-50",
    rail: "flex gap-x-3 pb-1 pl-9 pr-4",
    card: "w-[96px] aspect-[96/144]",
    count: 3,
    seeAll: false,
  },
};
